import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { ArrowLeft, Bot, Home } from 'lucide-react';
import FullScreenChat from '../components/AgentChat/FullScreenChat';
import { useChatSession } from '../hooks/useChatSession';

const AgentChatPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Memoize the onError function to prevent infinite loops
  const handleError = useCallback((err) => {
    console.error('Chat session error:', err);
  }, []);

  const {
    sessionId: currentSessionId,
    isLoading,
    error,
    isSessionReady,
    switchToSession,
    getSessions,
  } = useChatSession({
    autoLoadHistory: true,
    onError: handleError,
  });

  const [sessionTitle, setSessionTitle] = useState('AI Assistant');
  const [sessionsList, setSessionsList] = useState([]);
  const [showContent, setShowContent] = useState(false);

  // Fade in content when ready
  useEffect(() => {
    if (isSessionReady) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isSessionReady]);

  // Load specific session if sessionId provided in URL
  useEffect(() => {
    if (sessionId && sessionId !== currentSessionId && isSessionReady) {
      switchToSession(sessionId);
    }
  }, [sessionId, currentSessionId, isSessionReady, switchToSession]);

  // Load sessions list for navigation
  useEffect(() => {
    let isMounted = true;

    const loadSessions = async () => {
      try {
        const response = await getSessions();
        const sessions = response?.sessions || response || [];

        const transformedSessions = sessions.map(session => ({
          id: session.session_id,
          title: session.title || 'New Conversation',
          messageCount: session.message_count || 0,
          updatedAt: session.last_activity,
          createdAt: session.first_activity,
          lastMessage: session.last_message || 'No messages yet',
        }));

        if (isMounted) {
          setSessionsList(transformedSessions);

          if (currentSessionId && transformedSessions) {
            const currentSession = transformedSessions.find(s => s.id === currentSessionId);
            if (currentSession) {
              setSessionTitle(currentSession.title || 'AI Assistant');
            }
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to load sessions:', err);
        }
      }
    };

    if (isSessionReady) {
      loadSessions();
    }

    return () => {
      isMounted = false;
    };
  }, [currentSessionId, isSessionReady, getSessions]);

  const handleBackToHome = () => {
    navigate('/dashboard');
  };

  const handleSessionSwitch = (newSessionId) => {
    if (newSessionId !== currentSessionId) {
      navigate(`/agent-chat/${newSessionId}`);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="bg-card border-b px-6 py-4 flex items-center justify-between min-h-[80px] shadow-sm">
        <div className="flex items-center gap-4">
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToHome}
              className="text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}

          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-primary/10 rounded-full">
              <Bot className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">
                {sessionTitle}
              </h1>
              {/* Breadcrumbs */}
              <nav className="flex items-center text-sm text-muted-foreground">
                <button
                  onClick={handleBackToHome}
                  className="flex items-center gap-1 hover:text-foreground hover:underline"
                >
                  <Home className="h-4 w-4" />
                  Dashboard
                </button>
                <span className="mx-2">›</span>
                <span className="text-foreground">AI Assistant</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Session Status Indicator */}
        <div className="flex items-center gap-2">
          {isLoading && (
            <span className="text-sm text-muted-foreground">
              Loading...
            </span>
          )}
          {error && (
            <span className="text-sm text-destructive">
              Connection Error
            </span>
          )}
          {isSessionReady && !isLoading && !error && (
            <span className="text-sm text-success">
              Ready
            </span>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 overflow-hidden">
        <div
          className={cn(
            "h-full transition-opacity duration-300",
            showContent ? "opacity-100" : "opacity-0"
          )}
        >
          <FullScreenChat
            sessionId={currentSessionId}
            sessionsList={sessionsList}
            onSessionSwitch={handleSessionSwitch}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
};

export default AgentChatPage;
